import { useEffect, useRef, useSyncExternalStore } from 'react';

import type {
  FlagValueInterceptor,
  Value,
} from '@spotify/confidence-flag-evaluation-react';

export type PageFlag = {
  flag: string;
  readers: number;
  defaultValue: Value;
  resolvedValue: Value;
};

const EMPTY_SNAPSHOT: readonly PageFlag[] = Object.freeze([]);

const stableStringify = (value: unknown): string => {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value) ?? 'undefined';
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  const entries = Object.entries(value as Record<string, unknown>).sort(
    ([left], [right]) => left.localeCompare(right),
  );
  return `{${entries
    .map(([key, item]) => `${JSON.stringify(key)}:${stableStringify(item)}`)
    .join(',')}}`;
};

interface PageFlagRegistry {
  addReader(
    flag: string,
    defaultValue: Value,
    resolvedValue: Value,
  ): () => void;
  recordValue(flag: string, defaultValue: Value, resolvedValue: Value): void;
  subscribe(listener: () => void): () => void;
  getSnapshot(): readonly PageFlag[];
  getServerSnapshot(): readonly PageFlag[];
}

const scheduleFlush = (flush: () => void): void => {
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(flush);
    return;
  }
  setTimeout(flush, 0);
};

const createPageFlagRegistry = (): PageFlagRegistry => {
  const entries = new Map<string, PageFlag>();
  const listeners = new Set<() => void>();
  let snapshot: readonly PageFlag[] = EMPTY_SNAPSHOT;
  let scheduled = false;

  const flush = (): void => {
    scheduled = false;
    snapshot =
      entries.size === 0
        ? EMPTY_SNAPSHOT
        : [...entries.values()]
            .map(entry => ({ ...entry }))
            .sort((left, right) => left.flag.localeCompare(right.flag));
    listeners.forEach(listener => listener());
  };

  const schedule = (): void => {
    if (scheduled) {
      return;
    }
    scheduled = true;
    scheduleFlush(flush);
  };

  return {
    addReader(flag, defaultValue, resolvedValue) {
      const existing = entries.get(flag);
      if (existing) {
        existing.readers += 1;
      } else {
        entries.set(flag, { flag, readers: 1, defaultValue, resolvedValue });
      }
      schedule();

      return () => {
        const entry = entries.get(flag);
        if (!entry) {
          return;
        }
        entry.readers -= 1;
        if (entry.readers <= 0) {
          entries.delete(flag);
        }
        schedule();
      };
    },

    recordValue(flag, defaultValue, resolvedValue) {
      const entry = entries.get(flag);
      if (!entry) {
        return;
      }
      if (
        stableStringify(entry.resolvedValue) ===
          stableStringify(resolvedValue) &&
        stableStringify(entry.defaultValue) === stableStringify(defaultValue)
      ) {
        return;
      }
      entry.defaultValue = defaultValue;
      entry.resolvedValue = resolvedValue;
      schedule();
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    getSnapshot() {
      return snapshot;
    },

    getServerSnapshot() {
      return EMPTY_SNAPSHOT;
    },
  };
};

export const pageFlagRegistry = createPageFlagRegistry();

export const useRecordFlagRead: FlagValueInterceptor = <T extends Value>(
  flag: string,
  defaultValue: T,
  resolvedValue: T,
): T => {
  const defaultKey = stableStringify(defaultValue);
  const resolvedKey = stableStringify(resolvedValue);

  const latest = useRef({ defaultValue, resolvedValue });
  latest.current = { defaultValue, resolvedValue };

  useEffect(
    () =>
      pageFlagRegistry.addReader(
        flag,
        latest.current.defaultValue,
        latest.current.resolvedValue,
      ),
    [flag],
  );

  useEffect(() => {
    pageFlagRegistry.recordValue(
      flag,
      latest.current.defaultValue,
      latest.current.resolvedValue,
    );
  }, [flag, defaultKey, resolvedKey]);

  return resolvedValue;
};

export const usePageFlags = (): readonly PageFlag[] =>
  useSyncExternalStore(
    pageFlagRegistry.subscribe,
    pageFlagRegistry.getSnapshot,
    pageFlagRegistry.getServerSnapshot,
  );
